/* eslint-disable local-rules/set-state-in-use-effect */
import {useManualTimeout} from '@aboutyou/ui-components';
import {useRive, useViewModelInstanceBoolean} from '@rive-app/react-canvas';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSuccessMissionState} from '../../utils/useSuccessMissionState';
import RotatingCoinsRiv from '../assets/rotating-coins.riv';

const stateMachineName = 'State Machine B';
const getNotificationKey = (
  missionSuccessNotification: NonNullable<
    ReturnType<typeof useSuccessMissionState>['missionSuccessNotification']
  >,
) =>
  `${missionSuccessNotification.missionIdentifier.missionId}:${missionSuccessNotification.missionIdentifier.tierIndex}`;

export const useSuccessMissionPopup = ({
  autoCloseDelayMs = 6000,
  congratulationStageDelayMs = 1800,
}: {
  autoCloseDelayMs?: number | null;
  congratulationStageDelayMs?: number | null;
} = {}) => {
  const [canShowPopup, setCanShowPopup] = useState(false);
  const [step, setStep] = useState<'mission' | 'congratulation'>('mission');
  const lastNotificationKeyRef = useRef<string | undefined>(undefined);
  const pendingCloseNotificationKeyRef = useRef<string | undefined>(undefined);
  const [showPopupCycle, setShowPopupCycle] = useState(0);

  const [delayBeforeShowPopup, setDelayBeforeShowPopup] =
    useManualTimeout(1000);

  const [delayBeforeStartSpinAnimation, setDelayBeforeStartSpinAnimation] =
    useManualTimeout(300);

  const [
    delayBeforeShowCongratulationStage,
    setDelayBeforeShowCongratulationStage,
  ] = useManualTimeout(congratulationStageDelayMs);

  // auto close the popup after 6 seconds by default, null disables it
  const [delayBeforeClosePopup, setDelayBeforeClosePopup] =
    useManualTimeout(autoCloseDelayMs);

  const {
    missionSuccessNotification,
    shouldBeShownAfterToast,
    setMissionSuccessNotification,
    openerPosition,
  } = useSuccessMissionState();
  const missionSuccessNotificationKey = missionSuccessNotification
    ? getNotificationKey(missionSuccessNotification)
    : undefined;

  const handleOnClose = useCallback(() => {
    pendingCloseNotificationKeyRef.current = missionSuccessNotificationKey;
    setCanShowPopup(false);
  }, [missionSuccessNotificationKey]);

  const handleOnUnmount = useCallback(() => {
    const pendingCloseNotificationKey = pendingCloseNotificationKeyRef.current;
    pendingCloseNotificationKeyRef.current = undefined;

    if (!pendingCloseNotificationKey) {
      return;
    }

    setMissionSuccessNotification({
      missionSuccessNotification: undefined,
      expectedNotificationKey: pendingCloseNotificationKey,
    });
  }, [setMissionSuccessNotification]);

  const {rive, RiveComponent: RotatingCoins} = useRive(
    canShowPopup
      ? {
          src: RotatingCoinsRiv,
          autoBind: true,
          autoplay: true,
          stateMachines: stateMachineName,
        }
      : null,
    {
      // ensures the canvas matches the device's pixel density
      useDevicePixelRatio: true,
      shouldResizeCanvasToContainer: true,
    },
  );

  const viewModelInstance = rive?.viewModelInstance;

  const {setValue: setSpin} = useViewModelInstanceBoolean(
    'spin',
    viewModelInstance,
  );

  // Initialize the popup.
  useEffect(() => {
    if (canShowPopup) {
      const clearStartSpinAnimation = setDelayBeforeStartSpinAnimation();
      const clearShowCongratulationStage =
        setDelayBeforeShowCongratulationStage();
      const clearClosePopup = setDelayBeforeClosePopup();

      return () => {
        clearStartSpinAnimation?.();
        clearShowCongratulationStage?.();
        clearClosePopup?.();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canShowPopup,
    setDelayBeforeClosePopup,
    setDelayBeforeShowCongratulationStage,
    setDelayBeforeStartSpinAnimation,
  ]);

  // Start spinning animation of Rive file
  useEffect(() => {
    if (viewModelInstance) {
      setSpin(delayBeforeStartSpinAnimation);
    }
  }, [viewModelInstance, setSpin, delayBeforeStartSpinAnimation]);

  useEffect(() => {
    if (delayBeforeShowCongratulationStage && canShowPopup) {
      setStep('congratulation');
    }
  }, [canShowPopup, delayBeforeShowCongratulationStage]);

  // Start a new popup cycle only when a truly new mission notification arrives.
  useEffect(() => {
    if (!missionSuccessNotification) {
      lastNotificationKeyRef.current = undefined;
      return;
    }

    const isNewNotification =
      lastNotificationKeyRef.current !== missionSuccessNotificationKey;
    if (!isNewNotification) {
      return;
    }

    lastNotificationKeyRef.current = missionSuccessNotificationKey;
    setStep('mission');

    if (!shouldBeShownAfterToast) {
      setCanShowPopup(true);
      return;
    }

    if (!canShowPopup) {
      // If the popup is already visible, keep it mounted to avoid a hide/show flicker.
      setShowPopupCycle(current => current + 1);
    }
  }, [
    canShowPopup,
    missionSuccessNotificationKey,
    missionSuccessNotification,
    shouldBeShownAfterToast,
  ]);

  // Delay before showing the popup, usually after toast is shown.
  useEffect(() => {
    if (
      !missionSuccessNotification ||
      !shouldBeShownAfterToast ||
      canShowPopup ||
      showPopupCycle === 0
    ) {
      return;
    }

    return setDelayBeforeShowPopup();
  }, [
    canShowPopup,
    missionSuccessNotification,
    setDelayBeforeShowPopup,
    shouldBeShownAfterToast,
    showPopupCycle,
  ]);

  // If shouldBeShownAfterToast is true, the popup should be shown only after delayBeforeShowPopup.
  useEffect(() => {
    if (!missionSuccessNotification) {
      setCanShowPopup(false);
      return;
    }

    if (shouldBeShownAfterToast && delayBeforeShowPopup && !canShowPopup) {
      setCanShowPopup(true);
    }
  }, [
    canShowPopup,
    delayBeforeShowPopup,
    missionSuccessNotification,
    shouldBeShownAfterToast,
  ]);

  // Close the popup once the auto-close timer has elapsed.
  useEffect(() => {
    if (delayBeforeClosePopup && canShowPopup) {
      handleOnClose();
    }
  }, [canShowPopup, delayBeforeClosePopup, handleOnClose]);

  return {
    canShowPopup,
    handleOnClose,
    handleOnUnmount,
    missionSuccessNotification,
    openerPosition,
    RotatingCoins,
    step,
  };
};
